import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import {
  T, Icon, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn, stitchSectionLabel,
  stitchTooltipSlotProps,
} from '../ProjectOverview/parts/stitch';

/**
 * "Draaidagen koppelen" (projectflow, fase Productie — 28 sep 2026).
 *
 * De Productie-kaart zegt "geen draaidagen gekoppeld" zolang het Airtable-linkveld
 * `Project` op de draaidag leeg is. Hier zie je bovenaan wat al aan dit project hangt
 * (met Ontkoppelen, via bevestig) en daaronder de draaidagen zónder project uit de
 * afgelopen 30 en komende 120 dagen — suggesties (klant/projectnaam/-nummer in de
 * titel) bovenaan. Koppelen gaat één voor één; per regel zie je het resultaat, en een
 * draaidag die intussen aan een ander project hangt geeft een 409 met de reden.
 * Vangnet: de toast "Ongedaan maken" ontkoppelt precies de net gekoppelde draaidagen.
 */

const api = {
  async kandidaten(projectId) {
    const r = await authFetch(`${BACKEND_URL}/Api/draaidagen/koppel-kandidaten?projectId=${encodeURIComponent(projectId)}`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
    return d;
  },
  async koppel(draaidagId, projectId) {
    const r = await authFetch(`${BACKEND_URL}/Api/draaidagen/${draaidagId}/project`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(d.error || `HTTP ${r.status}`), { status: r.status });
    return d;
  },
  async ontkoppel(draaidagId, projectId) {
    const r = await authFetch(`${BACKEND_URL}/Api/draaidagen/${draaidagId}/project?projectId=${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(d.error || `HTTP ${r.status}`), { status: r.status });
    return d;
  },
};

export const formatDatum = (iso) => {
  if (!iso) return 'geen datum';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const REDEN_LABEL = { projectnummer: 'projectnummer', klant: 'klant', projectnaam: 'projectnaam' };

/** Zoekt op naam, titel, crew en datum (ISO én zoals getoond, bv. "5 okt"). */
export const past = (regel, zoek) => {
  const z = String(zoek || '').trim().toLowerCase();
  if (!z) return true;
  const hooi = [regel.naam, regel.titel, regel.datum, formatDatum(regel.datum), ...(regel.crew || [])]
    .join(' ').toLowerCase();
  return z.split(/\s+/).every((w) => hooi.includes(w));
};

function Regel({ regel, children, rechts }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
      borderRadius: '0.5rem', '&:hover': { bgcolor: T.surfaceLow } }}>
      {children}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.84rem', color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={regel.titel || regel.naam}>
          {regel.naam}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.secondary }}>
          {formatDatum(regel.datum)}{(regel.crew || []).length ? ` · ${regel.crew.join(', ')}` : ''}
        </Typography>
      </Box>
      {rechts}
    </Box>
  );
}

function DraaidagKoppelDialog({ open, onClose, projectRecId, onGewijzigd }) {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(null);
  const [zoek, setZoek] = useState('');
  const [selectie, setSelectie] = useState(() => new Set());
  const [resultaten, setResultaten] = useState({}); // id → { ok } | { fout }
  const [bezig, setBezig] = useState(false);

  const laad = useCallback(async () => {
    if (!projectRecId) return;
    setLaden(true); setFout(null);
    try { setData(await api.kandidaten(projectRecId)); }
    catch (e) { setFout(e.message); }
    finally { setLaden(false); }
  }, [projectRecId]);

  useEffect(() => {
    if (!open) return;
    setSelectie(new Set()); setResultaten({}); setZoek('');
    laad();
  }, [open, laad]);

  const gekoppeld = data?.gekoppeld || [];
  const kandidaten = useMemo(() => (data?.kandidaten || []).filter((r) => past(r, zoek)), [data, zoek]);
  const suggesties = kandidaten.filter((r) => r.score > 0);
  const overige = kandidaten.filter((r) => !(r.score > 0));

  const wissel = (id) => setSelectie((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const nadien = async () => {
    await laad();
    await onGewijzigd?.();
  };

  const koppelGeselecteerde = async () => {
    if (!selectie.size || bezig) return;
    setBezig(true);
    const gelukt = [];
    const uitkomst = {};
    try {
      // Eén voor één: elke draaidag wordt server-side vers gelezen, een 409 bij de
      // ene mag de rest niet tegenhouden.
      for (const id of selectie) {
        try {
          const d = await api.koppel(id, projectRecId);
          uitkomst[id] = { ok: true };
          // Alleen écht nieuwe koppelingen gaan in de undo: hing hij al aan dit project
          // (spiegel liep achter, of een collega was net eerder), dan is dat niet van ons
          // en mag ongedaan maken hem niet loshalen (Codex 28 sep).
          if (!d?.ongewijzigd) gelukt.push(id);
        } catch (e) {
          uitkomst[id] = { fout: e.message };
        }
        setResultaten((r) => ({ ...r, [id]: uitkomst[id] }));
      }
      setSelectie(new Set(Object.keys(uitkomst).filter((id) => !uitkomst[id].ok)));
      if (gelukt.length) {
        const n = gelukt.length;
        biedOngedaanAan(`${n} draaidag${n === 1 ? '' : 'en'} gekoppeld`, async () => {
          const mislukt = [];
          for (const id of gelukt) {
            try { await api.ontkoppel(id, projectRecId); } catch (e) { mislukt.push(e.message); }
          }
          await nadien();
          if (mislukt.length) throw new Error(`${mislukt.length} niet ontkoppeld: ${mislukt[0]}`);
        }, { eigenKnop: true });
      }
      if (Object.values(uitkomst).some((u) => u.ok)) await nadien();
      const fouten = Object.values(uitkomst).filter((u) => u.fout).length;
      if (fouten) toast.error(`${fouten} draaidag${fouten === 1 ? '' : 'en'} niet gekoppeld — zie de reden per regel`);
    } finally { setBezig(false); }
  };

  const ontkoppel = async (regel) => {
    if (!(await bevestig({
      titel: 'Draaidag ontkoppelen?',
      tekst: `${regel.naam} (${formatDatum(regel.datum)}) hangt daarna niet meer aan dit project. Je kunt hem hier later opnieuw koppelen.`,
      knop: 'Ontkoppelen',
    }))) return;
    try {
      await api.ontkoppel(regel.id, projectRecId);
      toast.success('Draaidag ontkoppeld');
      await nadien();
    } catch (e) { toast.error(`Ontkoppelen mislukt: ${e.message}`); }
  };

  const kandidaatRegel = (regel) => {
    const res = resultaten[regel.id];
    return (
      <Regel key={regel.id} regel={regel} rechts={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          {regel.score > 0 && (
            <Tooltip slotProps={stitchTooltipSlotProps}
              title={`Overeenkomst op ${(regel.redenen || []).map((x) => REDEN_LABEL[x] || x).join(', ')}`}>
              <Chip size="small" label="past bij klant/project"
                sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: T.tertiaryContainer + '55', color: T.tertiary }} />
            </Tooltip>
          )}
          {res?.fout && (
            <Typography variant="caption" role="alert" sx={{ fontFamily: T.fontBody, color: T.error, fontWeight: 700, maxWidth: 220 }}>
              {res.fout}
            </Typography>
          )}
        </Box>
      )}>
        <Checkbox size="small" checked={selectie.has(regel.id)} onChange={() => wissel(regel.id)}
          disabled={bezig} inputProps={{ 'aria-label': `${regel.naam} ${formatDatum(regel.datum)} selecteren` }} />
      </Regel>
    );
  };

  return (
    <Dialog open={open} onClose={bezig ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Draaidagen koppelen</DialogTitle>
      <DialogContent>
        {laden && !data && (
          <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size={22} /></Box>
        )}
        {fout && (
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.error, mb: 1 }}>
            Kon de draaidagen niet laden: {fout}
          </Typography>
        )}
        {data && (
          <>
            <Typography sx={{ ...stitchSectionLabel, mb: 0.5 }}>Gekoppeld aan dit project ({gekoppeld.length})</Typography>
            {gekoppeld.length === 0 ? (
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 2 }}>
                Nog geen draaidagen gekoppeld.
              </Typography>
            ) : (
              <Box sx={{ mb: 2 }}>
                {gekoppeld.map((regel) => (
                  <Regel key={regel.id} regel={regel} rechts={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {resultaten[regel.id]?.ok && (
                        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.emerald, fontWeight: 700 }}>
                          net gekoppeld
                        </Typography>
                      )}
                      <Box component="button" type="button" onClick={() => ontkoppel(regel)} disabled={bezig}
                        sx={{ ...stitchOutlineBtn, py: 0.3, px: 1, fontSize: '0.72rem', cursor: 'pointer' }}>
                        <Icon name="link_off" size={14} /> Ontkoppelen
                      </Box>
                    </Box>
                  )}>
                    <Icon name="event_available" size={18} sx={{ color: T.emerald }} />
                  </Regel>
                ))}
              </Box>
            )}

            <TextField size="small" fullWidth value={zoek} onChange={(e) => setZoek(e.target.value)}
              placeholder="Zoek op naam, crew of datum (bv. 5 okt)" sx={{ mb: 1.5 }}
              inputProps={{ 'aria-label': 'Zoek draaidag' }} />

            <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 1 }}>
              Draaidagen zonder project, van 30 dagen terug tot 120 dagen vooruit.
            </Typography>
            {suggesties.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ ...stitchSectionLabel, mb: 0.5 }}>Suggesties</Typography>
                {suggesties.map(kandidaatRegel)}
              </Box>
            )}
            {overige.length > 0 && (
              <Box>
                <Typography sx={{ ...stitchSectionLabel, mb: 0.5 }}>
                  {suggesties.length ? 'Overige draaidagen' : 'Draaidagen'}
                </Typography>
                {overige.map(kandidaatRegel)}
              </Box>
            )}
            {kandidaten.length === 0 && (
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                {zoek ? 'Niets gevonden voor deze zoekterm.' : 'Geen draaidagen zonder project in deze periode.'}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box component="button" type="button" onClick={onClose} disabled={bezig}
          sx={{ ...stitchOutlineBtn, cursor: 'pointer' }}>Sluiten</Box>
        <Box component="button" type="button" onClick={koppelGeselecteerde} disabled={!selectie.size || bezig}
          sx={{ ...stitchGradientBtn, border: 0, cursor: 'pointer', opacity: (!selectie.size || bezig) ? 0.5 : 1 }}>
          {bezig ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Icon name="link" size={16} />}
          Koppel geselecteerde{selectie.size ? ` (${selectie.size})` : ''}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default DraaidagKoppelDialog;
